import axios from 'axios';
import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import remarkGfm from 'remark-gfm';
import BusinessMetrics from '../components/BusinessMetrics';
import { TaskKanbanView, TaskListView } from '../components/GravityPlan';
import SegmentScoring from '../components/SegmentScoring';
import ResearchTab from '../components/ResearchTab';
import ResearchWidget from '../components/ResearchWidget';
import { ResearchEntityType } from '../services/researchService';
import {
  getFocusModeTailwindClasses,
  getSelectableFocusModes,
} from '../constants/focusModes';
import { useOrganization } from '../context/OrganizationContext';
import { useOrganizationAccess } from '../hooks/useOrganizationAccess';
import { groupsService } from '../services/groups';
import { MasterPlanTask, masterPlanTaskService } from '../services/masterPlan';
import { okrService, OrganizationOKR } from '../services/okrs';
import { GroupSummary } from '../types/groups';
import { formatCurrency as formatCurrencyUtil } from '../utils/currency';
import {
  formatSegmentPriority,
  formatMarketMaturity,
  formatFocusMode,
} from '../utils/enumFormatting';
import { API_URL } from '../constants/urls';

// Helper component for editing list fields
interface EditableListFieldProps {
  label: string;
  items: any[];
  onChange: (items: any[]) => void;
  color?: 'default' | 'primary' | 'secondary';
  placeholder?: string;
}

function EditableListField({
  label,
  items = [],
  onChange,
  color = 'default',
  placeholder = 'Add new item...',
}: EditableListFieldProps) {
  const [newItem, setNewItem] = useState('');

  // Normalize items to always work with the display value
  const normalizedItems = items.map((item) =>
    typeof item === 'string' ? item : item?.name || item,
  );

  const handleAdd = () => {
    if (newItem.trim()) {
      // Keep the same format as existing items (string vs object)
      const newItemToAdd =
        items.length > 0 && typeof items[0] === 'object'
          ? { name: newItem.trim() }
          : newItem.trim();

      onChange([...items, newItemToAdd]);
      setNewItem('');
    }
  };

  const handleRemove = (index: number) => {
    const updatedItems = items.filter((_, i) => i !== index);
    onChange(updatedItems);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  const colorClasses = {
    default: 'bg-gray-100 text-gray-700',
    primary: 'bg-blue-100 text-blue-700',
    secondary: 'bg-purple-100 text-purple-700',
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>

      {/* Display existing items */}
      {normalizedItems.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {normalizedItems.map((item, index) => (
            <span
              key={index}
              className={`inline-flex items-center px-2 py-1 rounded text-sm ${colorClasses[color]}`}
            >
              {item}
              <button
                onClick={() => handleRemove(index)}
                className="ml-2 hover:text-red-600"
              >
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Add new item */}
      <div className="flex gap-2">
        <input
          type="text"
          placeholder={placeholder}
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyPress={handleKeyPress}
          className="flex-1 px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          onClick={handleAdd}
          disabled={!newItem.trim()}
          className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Add
        </button>
      </div>
    </div>
  );
}

type Segment = {
  id: string;
  name: string;
  description?: string;
  focus_mode: string;
  priority: string;
  avg_obvious_score: number;
  company_count?: number;
  booked_business?: number;
  lost_business?: number;
  open_pipeline_unweighted?: number;
  open_pipeline_weighted?: number;
  strategic_owner_id?: string;
  owner_name?: string;

  // Business metrics
  prospect_count?: number;
  customer_count?: number;
  testimonial_count?: number;
  case_study_count?: number;
  award_count?: number;

  // Market Intelligence Fields
  total_addressable_market?: number;
  estimated_company_count?: number;
  market_growth_rate?: number;
  market_maturity?: string;
  key_competitors?: any[];
  market_trends?: any[];
  technology_trends?: any[];
  last_research_date?: string;
  research_frequency?: string;
  research_confidence_score?: number;
  research_sources?: any[];

  // Positioning Matrix Fields
  display_order?: number;
  group_name?: string;
  group_id?: string;
  group?: { id: string; name: string; color: string | null } | null;
};

export default function SegmentDetailTailwind() {
  const { segmentId } = useParams<{ segmentId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedOrg, canEdit, canDelete, isAdmin } = useOrganization();
  const { hasGenAIAccess, planType } = useOrganizationAccess();

  // Function to get initial tab from URL hash
  const getInitialTab = (): 'overview' | 'tasks' | 'research' => {
    const hash = location.hash.replace('#', '');
    if (hash === 'tasks' || hash === 'research') {
      return hash;
    }
    return 'overview';
  };

  const [segment, setSegment] = useState<Segment | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState<any>({});
  const [groups, setGroups] = useState<GroupSummary[]>([]);
  const [activeTab, setActiveTab] = useState<'overview' | 'tasks' | 'research'>(
    getInitialTab(),
  );
  const [tasks, setTasks] = useState<MasterPlanTask[]>([]);
  const [taskViewMode, setTaskViewMode] = useState<'kanban' | 'list'>('kanban');
  const [organizationMembers, setOrganizationMembers] = useState<any[]>([]);
  const [allOKRs, setAllOKRs] = useState<OrganizationOKR[]>([]);
  const [members, setMembers] = useState<any[]>([]);
  const [refreshResearchTab, setRefreshResearchTab] = useState(0);

  const organization = selectedOrg;

  // Function to handle tab changes and update URL
  const handleTabChange = (tab: 'overview' | 'tasks' | 'research') => {
    setActiveTab(tab);
    window.history.replaceState(null, '', `#${tab}`);
  };

  const fetchSegment = async () => {
    if (!organization || !segmentId) return;

    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/segments/${segmentId}?organization_id=${organization.id}`,
      );
      setSegment(response.data);
      setEditForm(response.data);
      setLoading(false);
    } catch (error: any) {
      // console.error removed

      // If we get a 404, it might be because we switched organizations
      // Navigate to the segments list page
      if (error.response?.status === 404) {
        // console.log removed
        navigate('/segments');
        return; // Don't set loading false, let navigation happen
      }
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    if (!organization) return;

    try {
      const response = await axios.get(
        `${API_URL}/organizations/${organization.id}/members?organization_id=${organization.id}`,
      );
      setMembers(response.data);
      setOrganizationMembers(response.data);
    } catch (error) {
      // console.error removed
    }
  };

  const fetchTasks = async () => {
    if (!organization || !segmentId) return;

    try {
      console.log('Fetching tasks for segment:', segmentId);
      const tasksData = await masterPlanTaskService.list(organization.id, {
        segment_ids: [segmentId],
        include_org_tasks: false, // Only show segment-specific tasks
      });
      console.log('Fetched tasks:', tasksData);
      setTasks(tasksData);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const fetchOKRs = async () => {
    if (!organization) return;

    try {
      const okrs = await okrService.listOKRs(organization.id);
      // Filter for only draft and active OKRs
      const activeOKRs = okrs.filter(
        (okr) => okr.status === 'draft' || okr.status === 'active',
      );
      setAllOKRs(activeOKRs);
    } catch (error) {
      console.error('Error fetching OKRs:', error);
      setAllOKRs([]);
    }
  };

  // Track previous organization to detect changes
  const prevOrgRef = React.useRef<string | null>(null);

  // Listen for hash changes (browser back/forward)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash === 'tasks' || hash === 'research') {
        setActiveTab(hash);
      } else {
        setActiveTab('overview');
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    // If organization changed, navigate to segments list
    if (
      prevOrgRef.current &&
      organization?.id &&
      prevOrgRef.current !== organization.id
    ) {
      // Organization changed logging removed
      navigate('/segments');
      return;
    }

    // Update the ref for next render
    if (organization?.id) {
      prevOrgRef.current = organization.id;
    }

    // Fetch data for current organization
    fetchSegment();
    fetchMembers();
    fetchTasks();
    fetchOKRs();
    if (organization) {
      groupsService
        .getGroupsSummary(organization.id, 'segment')
        .then(setGroups)
        .catch((_err) => {
          // Error fetching groups summary
        });
    }
  }, [organization, segmentId, navigate]);

  const handleSave = async () => {
    if (!segment || !organization) return;

    setSaving(true);
    try {
      const payload = {
        ...editForm,
        display_order: segment.display_order,
        group_id: editForm.group_id || undefined,
        // Convert empty string to undefined for strategic_owner_id
        strategic_owner_id: editForm.strategic_owner_id || undefined,
      };

      const response = await axios.put(
        `${API_URL}/segments/${segment.id}?organization_id=${organization.id}`,
        payload,
      );

      // Update local state with the response data to get the updated owner_name
      setSegment(response.data);
      setEditForm(response.data);
      setEditMode(false);
    } catch (error) {
      // console.error removed
    } finally {
      setSaving(false);
    }
  };

  const handleResearchComplete = (noteId: string) => {
    // Refresh the segment to get updated fields
    fetchSegment();
    // Trigger refresh of ResearchTab
    setRefreshResearchTab((prev) => prev + 1);
  };

  const formatCurrency = (value?: number) => {
    const currencyCode = organization?.currency || 'USD';
    return formatCurrencyUtil(value, currencyCode, {
      compact: true,
      maximumFractionDigits: 1,
    });
  };

  // Task handlers
  const handleTaskUpdate = async (
    taskId: string,
    updates: Partial<MasterPlanTask>,
  ) => {
    if (!organization) return;
    try {
      const updatedTask = await masterPlanTaskService.update(
        organization.id,
        taskId,
        updates,
      );
      setTasks(tasks.map((t) => (t.id === taskId ? updatedTask : t)));
    } catch (error) {
      // Error updating task
    }
  };

  const handleTaskCreate = async (task: MasterPlanTask) => {
    // After creating a task, refresh the tasks list to ensure proper filtering
    await fetchTasks();
  };

  const handleTaskDelete = async (taskId: string) => {
    if (!organization) return;
    try {
      await masterPlanTaskService.delete(organization.id, taskId);
      setTasks(tasks.filter((t) => t.id !== taskId));
    } catch (error) {
      // Error deleting task
    }
  };

  const formatPercentage = (value?: number) => {
    if (!value) return '-';
    return `${value.toFixed(1)}%`;
  };

  const formatLocalDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString + 'Z');
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="p-6">
        <p className="text-gray-600">Loading segment details...</p>
      </div>
    );
  }

  if (!segment) {
    return (
      <div className="p-6">
        <p className="text-red-600">Segment not found</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/segments')}
          className="mr-4 p-2 hover:bg-gray-100 rounded-full"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <h1 className="text-3xl font-bold flex-grow">{segment.name}</h1>
        <div className="flex gap-2">
          {editMode ? (
            <>
              <button
                onClick={() => setEditMode(false)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          ) : (
            canEdit && (
              <button
                onClick={() => setEditMode(true)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Edit Segment
              </button>
            )
          )}
        </div>
      </div>
      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => handleTabChange('overview')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'overview'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => handleTabChange('tasks')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'tasks'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Tasks
          </button>
          <button
            onClick={() => handleTabChange('research')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'research'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Research
          </button>
        </nav>
      </div>
      {/* Tab Content */}
      {activeTab === 'overview' && (
        <>
          {/* Business Metrics Section */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Business Metrics</h2>
            <BusinessMetrics
              prospectCount={segment.prospect_count}
              customerCount={segment.customer_count}
              testimonialCount={segment.testimonial_count}
              caseStudyCount={segment.case_study_count}
              awardCount={segment.award_count}
              organizationId={selectedOrg?.id || ''}
              segmentId={segmentId}
            />
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Core Segment Definition */}
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">
                  Basic Information
                </h3>
                {editMode ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        value={editForm.name || ''}
                        onChange={(e) =>
                          setEditForm({ ...editForm, name: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        value={editForm.description || ''}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            description: e.target.value,
                          })
                        }
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Focus Mode
                      </label>
                      <select
                        value={editForm.focus_mode || ''}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            focus_mode: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">None</option>
                        {getSelectableFocusModes().map((mode) => (
                          <option key={mode.value} value={mode.value}>
                            {mode.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Priority
                      </label>
                      <select
                        value={editForm.priority || ''}
                        onChange={(e) =>
                          setEditForm({ ...editForm, priority: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">None</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Strategic Owner
                      </label>
                      <select
                        value={editForm.strategic_owner_id || ''}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            strategic_owner_id: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Not assigned</option>
                        {members.map((member) => (
                          <option key={member.user_id} value={member.user_id}>
                            {member.user_name || member.user_email}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Group
                      </label>
                      <select
                        value={editForm.group_id || ''}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            group_id: e.target.value || undefined,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">No group</option>
                        {groups.map((group) => (
                          <option key={group.id} value={group.id}>
                            {group.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p>
                      <span className="font-medium">Description:</span>{' '}
                      {segment.description || 'No description'}
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-medium ${getFocusModeTailwindClasses(segment.focus_mode, true)}`}
                      >
                        Focus:{' '}
                        {formatFocusMode(segment.focus_mode) || 'Not Set'}
                      </span>
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-medium ${
                          segment.priority === 'high'
                            ? 'bg-red-100 text-red-800'
                            : segment.priority === 'medium'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        Priority:{' '}
                        {formatSegmentPriority(segment.priority) || 'Not Set'}
                      </span>
                    </div>
                    <p>
                      <span className="font-medium">Strategic Owner:</span>{' '}
                      {segment.owner_name || 'Not assigned'}
                    </p>
                    {segment.group && (
                      <p>
                        <span className="font-medium">Group:</span>{' '}
                        <span
                          className="inline-flex items-center px-2 py-1 rounded text-xs font-medium"
                          style={{
                            backgroundColor: segment.group.color
                              ? `${segment.group.color}20`
                              : '#f3f4f6',
                            color: segment.group.color || '#374151',
                          }}
                        >
                          {segment.group.name}
                        </span>
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Market Intelligence */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">
                  Market Intelligence
                </h3>

                {/* Market Sizing Section */}
                <div className="mb-6">
                  <h4 className="font-medium mb-3 text-gray-700">
                    Market Sizing
                  </h4>
                  {editMode ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          Total Addressable Market ($)
                        </label>
                        <input
                          type="number"
                          value={editForm.total_addressable_market || ''}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              total_addressable_market: Number(e.target.value),
                            })
                          }
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          Estimated Company Count
                        </label>
                        <input
                          type="number"
                          value={editForm.estimated_company_count || ''}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              estimated_company_count: Number(e.target.value),
                            })
                          }
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          Market Growth Rate (%)
                        </label>
                        <input
                          type="number"
                          value={editForm.market_growth_rate || ''}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              market_growth_rate: Number(e.target.value),
                            })
                          }
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          Market Maturity
                        </label>
                        <select
                          value={editForm.market_maturity || ''}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              market_maturity: e.target.value,
                            })
                          }
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">None</option>
                          <option value="emerging">Emerging</option>
                          <option value="growing">Growing</option>
                          <option value="mature">Mature</option>
                          <option value="declining">Declining</option>
                        </select>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">TAM</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {formatCurrency(segment.total_addressable_market)}
                        </p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">
                          Est. Companies
                        </p>
                        <p className="text-sm font-semibold text-gray-900">
                          {segment.estimated_company_count?.toLocaleString() ||
                            '-'}
                        </p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">
                          Growth Rate
                        </p>
                        <p className="text-sm font-semibold text-gray-900">
                          {formatPercentage(segment.market_growth_rate)}
                        </p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Maturity</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {formatMarketMaturity(segment.market_maturity) ||
                            'Not specified'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Competitive Analysis Section */}
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3 text-gray-700">
                    Competitive Analysis
                  </h4>
                  {editMode ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <EditableListField
                        label="Key Competitors"
                        items={editForm.key_competitors || []}
                        onChange={(items) =>
                          setEditForm({ ...editForm, key_competitors: items })
                        }
                        placeholder="Add competitor..."
                      />
                      <EditableListField
                        label="Market Trends"
                        items={editForm.market_trends || []}
                        onChange={(items) =>
                          setEditForm({ ...editForm, market_trends: items })
                        }
                        color="primary"
                        placeholder="Add market trend..."
                      />
                      <EditableListField
                        label="Technology Trends"
                        items={editForm.technology_trends || []}
                        onChange={(items) =>
                          setEditForm({ ...editForm, technology_trends: items })
                        }
                        color="secondary"
                        placeholder="Add tech trend..."
                      />
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <h5 className="text-sm font-medium text-gray-700 mb-2">
                          Key Competitors
                        </h5>
                        {segment.key_competitors &&
                        segment.key_competitors.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {segment.key_competitors.map(
                              (competitor, index) => (
                                <span
                                  key={index}
                                  className="inline-block px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm"
                                >
                                  {competitor.name || competitor}
                                </span>
                              ),
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">
                            No competitors identified
                          </p>
                        )}
                      </div>
                      <div>
                        <h5 className="text-sm font-medium text-gray-700 mb-2">
                          Market Trends
                        </h5>
                        {segment.market_trends &&
                        segment.market_trends.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {segment.market_trends.map((trend, index) => (
                              <span
                                key={index}
                                className="inline-block px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm"
                              >
                                {trend.name || trend}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">
                            No trends identified
                          </p>
                        )}
                      </div>
                      <div>
                        <h5 className="text-sm font-medium text-gray-700 mb-2">
                          Technology Trends
                        </h5>
                        {segment.technology_trends &&
                        segment.technology_trends.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {segment.technology_trends.map((trend, index) => (
                              <span
                                key={index}
                                className="inline-block px-2 py-1 bg-purple-100 text-purple-700 rounded text-sm"
                              >
                                {trend.name || trend}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">
                            No tech trends identified
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* AI Research Widget */}
              {segment && organization && (
                <ResearchWidget
                  segmentId={segment.id}
                  organizationId={organization.id}
                  canEdit={canEdit}
                  hasGenAIAccess={hasGenAIAccess}
                  planType={planType}
                  isAdmin={isAdmin}
                  onResearchComplete={handleResearchComplete}
                />
              )}
            </div>

            {/* Right Column - Performance & Scoring */}
            <div className="space-y-6">
              {/* Financial Metrics */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">
                  Financial Metrics
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-2xl font-bold text-blue-600">
                      {segment.company_count || 0}
                    </p>
                    <p className="text-sm text-gray-600">Companies</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">
                      {formatCurrency(segment.booked_business)}
                    </p>
                    <p className="text-sm text-gray-600">Booked Business</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-600">
                      {formatCurrency(segment.lost_business)}
                    </p>
                    <p className="text-sm text-gray-600">Lost Business</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-yellow-600">
                      {formatCurrency(segment.open_pipeline_unweighted)}
                    </p>
                    <p className="text-sm text-gray-600">Open Pipeline</p>
                  </div>
                </div>
              </div>

              {/* Obvious Choice Scoring */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">
                  Obvious Choice Scoring
                </h3>
                {segmentId && organization && (
                  <SegmentScoring
                    segmentId={segmentId}
                    organizationId={organization.id}
                    readOnly={!canEdit}
                  />
                )}
              </div>
            </div>
          </div>
        </>
      )}
      {/* Tasks Tab */}
      {activeTab === 'tasks' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Tasks</h2>
            <div className="flex gap-2">
              <button
                onClick={() =>
                  setTaskViewMode(taskViewMode === 'kanban' ? 'list' : 'kanban')
                }
                className="p-2 border border-gray-300 rounded hover:bg-gray-50"
                title={
                  taskViewMode === 'kanban'
                    ? 'Switch to List View'
                    : 'Switch to Kanban View'
                }
              >
                {taskViewMode === 'kanban' ? (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 10h16M4 14h16M4 18h16"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {taskViewMode === 'kanban' ? (
            <TaskKanbanView
              tasks={tasks}
              onTaskUpdate={handleTaskUpdate}
              onTaskCreate={handleTaskCreate}
              onTaskDelete={handleTaskDelete}
              organizationId={selectedOrg?.id || ''}
              entityType="segment"
              entityId={segmentId}
              canEdit={canEdit}
              segments={segment ? [segment] : []}
              propositions={[]}
              positions={[]}
              groups={[]}
              companies={[]}
            />
          ) : (
            <TaskListView
              tasks={tasks}
              onTaskUpdate={handleTaskUpdate}
              onTaskCreate={handleTaskCreate}
              onTaskDelete={handleTaskDelete}
              organizationId={selectedOrg?.id || ''}
              entityType="segment"
              entityId={segmentId}
              canEdit={canEdit}
              segments={segment ? [segment] : []}
              propositions={[]}
              positions={[]}
              groups={[]}
              companies={[]}
            />
          )}
        </div>
      )}
      {/* Research Tab */}
      {activeTab === 'research' && segment && organization && (
        <ResearchTab
          organizationId={organization.id}
          entityType={ResearchEntityType.SEGMENT}
          entityId={segment.id}
          entityName={segment.name}
          canEdit={canEdit}
        />
      )}
    </div>
  );
}
